/*
 * AutoColorOnline – Build Your Own Kit
 *
 * Flow: Primer → Paint → Clear → Anything else.
 * Nothing is pre-added to the kit. A product only enters the kit when the
 * shopper picks it (or arrives via ?product=handle&variant=id from a product page).
 *
 * Data: assets/aco-kit-catalog.json seeds the product lists. Live prices,
 * availability, images, stock messages and benefit lines for every kit product
 * arrive in ONE request from /collections/kit-builder-feed?view=aco-kit-feed
 * (data-feed-url). Per-product requests (/products/{handle}?view=aco-stock,
 * falling back to /products/{handle}.js) are only used for products missing
 * from the feed. Custom OMNI colors come from /search?view=aco-kit.
 */
(async () => {
  const root = document.getElementById('aco-kit-blue');
  if (!root || root.dataset.initialized) return;
  root.dataset.initialized = 'true';
  const el = id => root.querySelector('#' + id);

  /* ---------- catalog + feed (fetched in parallel) ---------- */
  const feedUrl = root.dataset.feedUrl || '';
  // The first feed request matches the <link rel="preload"> in the section; `fresh` bypasses caches for the pre-checkout re-check.
  const fetchFeed = (fresh = false) => feedUrl
    ? fetch(fresh ? feedUrl + (feedUrl.includes('?') ? '&' : '?') + '_=' + Date.now() : feedUrl, fresh ? { cache: 'no-store' } : {}).then(r => r.ok ? r.json() : null).catch(() => null)
    : Promise.resolve(null);
  const feedRequest = fetchFeed();
  let data;
  try {
    const response = await fetch(root.dataset.catalogUrl);
    if (!response.ok) throw Error('Catalog unavailable');
    data = await response.json();
  } catch (e) {
    el('aco-status').textContent = 'Could not load the kit builder. Please refresh to try again.';
    el('aco-cart').disabled = true;
    return;
  }

  /* ---------- helpers ---------- */
  const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const norm = s => String(s).normalize('NFKD').toLowerCase().replace(/[^a-z0-9]/g, '');
  const money = n => Number.isFinite(n)
    ? new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: root.dataset.currency || 'USD' }).format(n)
    : 'Updating…';
  const clamp = n => Math.max(1, Math.min(99, Math.floor(Number(n) || 1)));
  const numericId = id => String(id).split('/').pop();
  const shopRoot = root.dataset.root || '/';

  /* ---------- state ---------- */
  // Custom (OMNI) paints: [id, make, code, color, handle, variants[[id, kind, size, price, available]]]
  const custom = data.custom.map(p => ({ id: p[0], make: p[1], code: p[2], color: p[3], handle: p[4], variants: p[5], key: norm(p[1] + ' ' + p[2] + ' ' + p[3]) }));

  // Paint is OFF until the shopper chooses a color. `factory` / `custom` hold the chosen product.
  const paint = { on: false, source: 'factory', factory: null, kind: 'base', custom: null, size: 4, qty: 1, include: true };
  let resultLimit = 8;
  const removedKitItems = new Map();

  const coatingSets = ['clear', 'primer'].map(id => ({
    id,
    label: id === 'clear' ? 'Clearcoat' : 'Primer',
    types: id === 'clear' ? data.clearTypes : data.primerTypes,
    products: data.coatings.filter(p => p.kind === id && !/\(POS\)/i.test(p.title))
  }));
  // Coatings are also OFF until picked; the preselected id is only the first tile highlighted when turned on.
  const states = Object.fromEntries(coatingSets.map(c => [c.id, {
    on: false,
    id: (c.id === 'clear' ? c.products.find(p => p.title.startsWith('High Teck 8100')) : c.products.find(p => p.title.startsWith('High Teck 7030')))?.id || c.products[0].id,
    variant: 0, qty: 1, type: '', limit: 6
  }]));

  const extraProducts = data.accessories.filter(p => p.variants.some(v => v.available));
  const extras = extraProducts.flatMap(p => p.variants.filter(v => v.available).map(v => ({ ...v, productId: p.id, name: p.title, category: p.category, url: p.url, handle: p.handle })));
  const extraQty = {};
  const extraChoice = Object.fromEntries(extraProducts.map(p => [p.id, p.variants.find(v => v.available).id]));
  let extraLimit = 6;

  const liveProducts = new Map(), liveById = new Map(), variantIndex = new Map(), pendingProducts = new Map(), queued = new Set(), queue = [];
  let busy = false, refreshTimer, customTimer, customRequest = 0, customLoadedQuery = '', activeLoads = 0;
  const companionChoices = {}, companionIncluded = { primer: true, clear: true };
  const allSeed = [...data.factory, ...data.coatings, ...data.accessories, ...data.companions];
  // Seed prices are placeholders until live data arrives.
  allSeed.forEach(p => p.variants.forEach(v => v.price = NaN));
  extras.forEach(v => v.price = NaN);
  custom.forEach(p => p.variants.forEach(v => v[3] = NaN));
  const kitHandle = data.paintKit.handle || new URL(data.paintKit.onlineStoreUrl).pathname.split('/').pop();

  /* ---------- paint selectors ---------- */
  const factory = () => paint.factory ? data.factory.find(p => p.code === paint.factory) : null;
  const paintProduct = () => paint.source === 'factory' ? factory() : paint.custom;
  const isBase = () => paint.source === 'factory' || paint.kind === 'base';
  const customVariants = p => p.variants.filter(v => v[1] === (paint.kind === 'base' ? 0 : 1) && v[4]);
  const paintVariant = () => paint.custom ? customVariants(paint.custom).find(v => v[2] === paint.size) : null;
  const factoryVariant = () => factory()?.variants.find(v => v.size === paint.size);
  const paintVariantId = () => paint.source === 'factory' ? factoryVariant()?.id : paintVariant()?.[0];
  const sizes = () => paint.source === 'factory'
    ? (factory()?.variants.filter(v => v.available !== false).map(v => [v.size, v.label]) || [])
    : paint.custom ? customVariants(paint.custom).map(v => [v[2], v[2] === 4 ? 'Gallon' : v[2] === 1 ? 'Quart' : 'Pint']) : [];

  function paintSuppliesIncluded() { return paint.include && !removedKitItems.has(String(paintRecommendation().id)); }
  function setPaintSuppliesIncluded(include) { paint.include = include; if (include) removedKitItems.delete(String(paintRecommendation().id)); }
  function paintCost() {
    if (!paintProduct()) return 0;
    if (paint.source === 'custom' && !paintVariant()) return 0;
    const unit = paint.source === 'factory' ? factoryVariant()?.price : paintVariant()[3];
    return (unit + (paintSuppliesIncluded() ? paintRecommendation().price : 0)) * paint.qty;
  }

  const factorySwatches = { HFP250: '#222428', HFP254: '#666b6b', HFP350: '#b9bcba', HFP360: '#d0d2ce', HFP150: '#faf9f2', HFP151: '#efefdf', HFP154: '#f9f5e9', HFP158: '#f8f8f6', HFP161: '#e6e4d6', HFP450: '#b32430', HFP451: '#cf2933', HFP454: '#713c43', HFP550: '#e1743e', HFP655: '#b1a392', HFP856: '#2e3e5a' };

  /* ---------- paint UI ---------- */
  function factoryTiles() {
    data.factory.forEach(queueProduct);
    const q = el('aco-paint-search').value.trim().split(/\s+/).map(norm);
    el('aco-factory-colors').innerHTML = data.factory
      .filter(p => q.every(t => norm(p.name + p.code + p.vehicle).includes(t)))
      .map(p => {
        const picked = paint.on && paint.factory === p.code;
        return `<button type="button" class="tile" data-factory="${p.code}" aria-pressed="${picked}"><span class="swatch" aria-hidden="true" style="background:${factorySwatches[p.code]}"></span><span class="kit-product-heading">${productThumb(p)}<strong>${esc(p.name)}</strong></span><span>${p.code} · ${esc(p.vehicle)}</span><span class="selected-mark">${picked ? 'Selected' : p.variants.map(v => v.label).join(' / ')}</span></button>`;
      }).join('') || '<p>No matching factory colors.</p>';
  }

  function searchPaint(q) {
    const tokens = q.trim().split(/\s+/).map(norm).filter(Boolean);
    if (!tokens.length) return [];
    const nq = norm(q);
    return custom
      .filter(p => p.searchMatch && p.live && customVariants(p).length && tokens.every(t => p.key.includes(t)))
      .map(p => ({ p, score: norm(p.make + p.code) === nq ? 0 : norm(p.code) === nq ? 1 : norm(p.code).startsWith(nq) ? 2 : 3 }))
      .sort((a, b) => a.score - b.score || a.p.make.localeCompare(b.p.make) || a.p.code.localeCompare(b.p.code))
      .map(x => x.p);
  }

  function renderCustomResults() {
    const q = el('aco-paint-search').value.trim();
    if (!q) { el('aco-custom-results').innerHTML = '<p>Enter a make, paint code, or color name above.</p>'; return; }
    const found = searchPaint(q);
    el('aco-custom-results').innerHTML = found.length
      ? `<p>${found.length} matching color${found.length === 1 ? '' : 's'}</p>` + found.slice(0, resultLimit).map(p => `<div class="result">${productThumb(p, customVariants(p)[0]?.[0])}<div class="kit-search-copy"><strong>${esc(p.make + ' ' + p.code)}</strong><br>${esc(p.color)}<br>From ${money(Math.min(...customVariants(p).map(v => v[3])))}</div><button type="button" data-custom="${p.id}">Choose paint</button></div>`).join('') + (found.length > resultLimit ? '<button type="button" data-more-paint>Show more matches</button>' : '')
      : `<div class="recommended"><strong>No matching ${paint.kind === 'base' ? 'basecoat' : 'single-stage'} listing found.</strong><a href="${data.finder}" target="_blank" rel="noopener noreferrer">Open OEM Vehicle Paint Finder</a></div>`;
  }

  function drawPaint() {
    const product = paintProduct();
    queueProduct(product);
    el('aco-paint-on').checked = paint.on;
    el('aco-paint-search').placeholder = paint.source === 'factory' ? 'Search factory color or HFP code' : 'Search make + code, e.g. Toyota 6V7';
    el('aco-factory-section').hidden = paint.source !== 'factory';
    el('aco-custom-section').hidden = paint.source !== 'custom';
    factoryTiles();
    const selectedBox = el('aco-paint-selected');

    if (!paint.on || !product) { selectedBox.innerHTML = ''; total(); return; }
    if (paint.source === 'factory' && product.variants.every(v => v.available === false)) {
      selectedBox.innerHTML = '<p class="error">This factory color is currently unavailable. Choose another color to continue.</p>';
      total(); return;
    }
    const options = sizes();
    if (options.length && !options.some(([s]) => s === paint.size)) paint.size = options.find(([s]) => s === 4)?.[0] || options[0][0];
    if (paint.source === 'custom' && !paintVariant()) {
      selectedBox.innerHTML = `<p>This paint is not available in the selected system. <a href="${data.finder}" target="_blank" rel="noopener noreferrer">Open OEM Vehicle Paint Finder</a></p>`;
      total(); return;
    }

    const rec = paintRecommendation(), base = isBase(), variantId = paintVariantId();
    const name = paint.source === 'factory' ? product.name + ' · ' + paint.factory : product.make + ' ' + product.code + ' ' + product.color + ' · OMNI ' + (base ? 'MBC' : 'MTK');
    const link = paint.source === 'factory' ? product.url : shopRoot + 'products/' + product.handle + '?variant=' + variantId;
    selectedBox.innerHTML = `<div class="selected">
      <div class="product-head"><span class="kit-product-heading">${productThumb(product, variantId)}<strong>${esc(name)}</strong></span><span class="pill">Your paint</span></div>
      <div class="controls"><label>Size <select data-paint-size>${options.map(([s, l]) => `<option value="${s}" ${s === paint.size ? 'selected' : ''}>${l}</option>`).join('')}</select></label><label>Quantity <input class="qty" type="number" min="1" max="99" data-paint-qty value="${paint.qty}"></label></div>
      ${stockMarkup(product, variantId)}
      ${benefitList(paint.source === 'factory' ? product : { benefits: customPaintBenefits(base) }, 'About this paint')}
      <div class="recommended"><strong>Recommended with your paint</strong><div class="kit-product-heading">${productThumb({ handle: rec.handle }, rec.id)}<div><div>${rec.name}</div><div>${esc(rec.variant)} × ${paint.qty}</div>${stockMarkup(rec, rec.id)}</div></div><label class="include-choice"><input type="checkbox" data-paint-include ${paintSuppliesIncluded() ? 'checked' : ''}><span>Add recommended supplies</span>${paintSuppliesIncluded() ? '<span class="pill">Included</span>' : ''}</label></div>
      <div class="mix">${base ? 'Mix equal parts paint and reducer. <strong>1:1</strong>' : 'Mix 4 parts paint, 1 part hardener, and 1 part reducer. <strong>4:1:1</strong>'}</div>
      <details><summary>Mixing guide &amp; package details</summary><div class="detail-content"><p>${rec.note}</p><a href="${link}" target="_blank" rel="noopener noreferrer">View selected paint</a></div></details>
      <p class="note">Paint and selected supplies: <strong>${money(paintCost())}</strong></p>
    </div>`;
    const clearNote = el('aco-clear-note');
    if (clearNote) clearNote.textContent = base ? 'Add shine and protection over your basecoat.' : 'Optional with single-stage paint.';
    total();
  }

  function choosePaint(update) {
    Object.assign(paint, update, { on: true });
    el('aco-custom-results').innerHTML = '';
    drawPaint();
  }

  /* ---------- coatings (primer / clear) ---------- */
  function selected(c) {
    const s = states[c.id], p = c.products.find(p => p.id === s.id), variants = p.variants.filter(v => v.available);
    if (!variants.length) variants.push({ ...p.variants[0], available: false, price: NaN });
    if (s.variant >= variants.length) s.variant = 0;
    return { s, p, variants, v: variants[s.variant] };
  }
  function defaultVariant(p) {
    const vs = p.variants.filter(v => v.available), index = vs.findIndex(v => /medium|normal/i.test(v.title));
    if (index >= 0) return index;
    return Math.max(0, vs.findIndex(v => !/^(none|no hardener)$/i.test(v.title)));
  }
  for (const c of coatingSets) states[c.id].variant = defaultVariant(selected(c).p);

  el('aco-coating-sections').innerHTML = coatingSets.map(c => `<section class="section" id="aco-pane-${c.id}" role="tabpanel" aria-labelledby="aco-tab-${c.id}" hidden>
    <div class="section-head"><div><h2>${c.id === 'clear' ? 'Choose your finish' : 'Build a solid foundation'}</h2><p>${c.id === 'clear' ? 'Select your clearcoat and matching hardener.' : 'Find the right primer for your preparation.'}</p></div><label class="check"><input type="checkbox" data-coating-on="${c.id}">Include ${c.label.toLowerCase()}</label></div>
    <div class="searchline"><label>Find a product<input id="aco-search-${c.id}" class="search" type="search" data-coating-search="${c.id}" placeholder="Search ${c.label.toLowerCase()} or product number" aria-label="Search ${c.label}"></label><label>${c.label} type <select data-coating-type="${c.id}"><option value="">All types (${c.products.length})</option>${c.types.map(t => `<option value="${t}">${t} (${c.products.filter(p => p.types.includes(t)).length})</option>`).join('')}<option value="other">Other / general tags (${c.products.filter(p => !p.types.length).length})</option></select></label></div>
    <p id="aco-${c.id}-note" class="hint">${c.id === 'clear' ? 'Add shine and protection over your basecoat.' : 'Choose the primer type, then choose your product.'}</p>
    <div id="aco-count-${c.id}" aria-live="polite"></div><div class="tile-grid" id="aco-tiles-${c.id}"></div><div id="aco-more-${c.id}"></div><div id="aco-selected-${c.id}"></div>
  </section>`).join('');

  function drawCoatingTiles(c) {
    const s = states[c.id], tokens = el('aco-search-' + c.id).value.trim().split(/\s+/).map(norm).filter(Boolean);
    const found = c.products
      .filter(p => p.variants.some(v => v.available) && (!s.type || (s.type === 'other' && !p.types.length) || p.types.includes(s.type)) && tokens.every(t => norm(p.title + ' ' + p.variants.map(v => v.title).join(' ')).includes(t)))
      .sort((a, b) => (b.id === s.id) - (a.id === s.id) || a.title.localeCompare(b.title));
    found.slice(0, s.limit).forEach(queueProduct);
    el('aco-count-' + c.id).textContent = found.length + ' matching products';
    el('aco-tiles-' + c.id).innerHTML = found.slice(0, s.limit).map(p => {
      const picked = s.on && s.id === p.id;
      return `<button type="button" class="tile" data-coating-pick="${c.id}" data-id="${p.id}" aria-pressed="${picked}"><span class="kit-product-heading">${productThumb(p)}<strong>${esc(p.title)}</strong></span>${benefitLine(p)}<span>From ${money(Math.min(...p.variants.filter(v => v.available).map(v => v.price)))}</span><span class="selected-mark">${picked ? 'Selected' : 'Choose'}</span></button>`;
    }).join('') || '<p>No available products match this type and search.</p>';
    el('aco-more-' + c.id).innerHTML = found.length > s.limit ? `<button type="button" data-more-coating="${c.id}">Show more products</button>` : '';
  }

  function packageInfo(p, v) {
    if (/(?:^|\/)\s*(?:none|no hardener)\s*$/i.test(v.title)) return 'Hardener not included. Use the matching hardener specified in the product instructions.';
    if (p.mix.companion === 'none') return 'No hardener needed.';
    if (p.mix.companion === 'internal') return /2K/i.test(p.title) ? 'Activate the 2K can as directed; no separate hardener is added.' : 'Ready-to-use aerosol; no separate hardener is added.';
    if (/100712/.test(p.title)) return 'Includes one 0.75 fl. oz. tube of liquid hardener per quart.';
    if (/MP213/.test(p.title)) return 'Matching reducer: MR185, MR186, MR187, or MR188, sold separately. No hardener needed.';
    if (/^(?:MBC|MTK)/.test(v.title)) return v.title;
    if (v.title !== 'Default Title' && !/^(quart|gallon|pint)$/i.test(v.title)) return v.title;
    if (/MP17[012]/.test(p.title)) return /kit/i.test(p.title) ? 'MP175 catalyst included in the quantities shown in this kit.' : 'Matching catalyst: MP175, sold separately.';
    if (/7755[13]/.test(p.title)) return /kit|w\/|&/i.test(p.title) ? '77557 activator included as listed.' : 'Matching activator: 77557, sold separately.';
    if (/750[012]/.test(p.title)) return '7503 catalyst included as listed.';
    if (/77424/.test(p.title)) return '77426 activator included: 1 quart.';
    if (/7330/.test(p.title)) return 'Matching activator is included in the selected package; see product details.';
    if (/77165/.test(p.title)) return /kit/i.test(p.title) ? '7102 activator included as listed.' : 'Matching activator: 7102, sold separately.';
    if (/MP3-HS/.test(p.title)) return 'Matching activator: MA3-HS, sold separately.';
    if (/8550/.test(p.title)) return '8551 activator included as listed.';
    if (/kit|w\/|with hardener|w\/activator/i.test(p.title)) return 'Includes the companion products specified in the product title and selected option.';
    return 'Product only. Check the mixing guide for required companion products.';
  }

  function drawCoating(c) {
    drawCoatingTiles(c);
    const { s, p, v, variants } = selected(c);
    if (s.on) queueProduct(p);
    root.querySelector('[data-coating-on="' + c.id + '"]').checked = s.on;
    const box = el('aco-selected-' + c.id);
    box.hidden = !s.on;
    box.innerHTML = `<div class="selected">
      <div class="product-head"><span class="kit-product-heading">${productThumb(p, v.id)}<strong>${esc(p.title)}</strong></span><span class="pill">In your kit</span></div>
      <div class="controls">${variants.length > 1 ? `<label>Size / hardener option <select data-coating-variant="${c.id}" aria-label="${c.label} size and hardener">${variants.map((x, i) => `<option value="${i}" ${i === s.variant ? 'selected' : ''}>${esc(x.title)} — ${money(x.price)}</option>`).join('')}</select></label>` : ''}<label>Quantity <input class="qty" type="number" min="1" max="99" value="${s.qty}" data-coating-qty="${c.id}"></label></div>
      ${stockMarkup(p, v.id)}
      ${benefitList(p)}
      <div class="recommended"><strong>Selected supplies</strong><p>${esc(v.available ? packageInfo(p, v) : 'This product is currently unavailable. Choose another product.')}</p>${companionMarkup(c)}</div>
      <div class="mix">${esc(p.mix.text)}</div>
      <details><summary>Package and mixing details</summary><div class="detail-content"><a href="${p.url}?variant=${v.id}" target="_blank" rel="noopener noreferrer">Open product instructions and selected package</a>${p.mix.source ? `<p><a href="${p.mix.source}" target="_blank" rel="noopener noreferrer">Mixing instructions</a></p>` : ''}</div></details>
      <p class="note">${c.label} selection: <strong>${money(coatingCost(c))}</strong></p>
    </div>`;
    total();
  }

  /* ---------- totals / summary ---------- */
  function total() {
    const lines = selectedLines();
    let amount;
    if (lines) amount = lines.reduce((n, l) => n + l.price * l.quantity, 0);
    else {
      // Live data still loading: estimate from seed prices.
      amount = paint.on ? paintCost() : 0;
      for (const c of coatingSets) if (states[c.id].on) amount += coatingCost(c);
      amount += extras.reduce((sum, x) => sum + (extraQty[x.id] ? extraQty[x.id] * x.price : 0), 0);
    }
    el('aco-total').innerHTML = '<span class="note">Kit subtotal</span><strong>' + money(amount) + '</strong>';
    kitSummary();
    const missingPaint = paint.on && paint.source === 'custom' && !paintVariant();
    const nothingChosen = !paint.on && !Object.values(states).some(s => s.on) && !Object.values(extraQty).some(Boolean);
    el('aco-cart').disabled = busy || !selectionReady() || missingPaint || nothingChosen;
  }

  /* ---------- extras ---------- */
  function extraMatches() {
    const tokens = el('aco-extra-search').value.trim().split(/\s+/).map(norm).filter(Boolean), type = el('aco-extra-type').value;
    const found = extraProducts.filter(p => p.variants.some(v => v.available) && (!type || p.category === type) && tokens.every(t => norm(p.title + ' ' + p.category + ' ' + p.variants.map(v => v.title).join(' ')).includes(t)));
    // Interleave categories so one category does not crowd out the rest.
    const groups = data.accessoryCategories.map(c => found.filter(p => p.category === c)), result = [];
    for (let i = 0; groups.some(g => i < g.length); i++) for (const g of groups) if (g[i]) result.push(g[i]);
    return result;
  }
  const shortSupply = title => title.replace(/[®™]/g, '').replace(/\s+/g, ' ').replace(/,?\s*(?:EVA Rubber|Purple, Dry, Hookit Attachment|Sun Yellow \(Tape\), Clear \(Film\))/g, '').trim();

  function drawExtras() {
    const found = extraMatches();
    found.slice(0, extraLimit).forEach(queueProduct);
    el('aco-extra-count').textContent = found.length + ' available products';
    el('aco-extra-results').innerHTML = found.slice(0, extraLimit).map(p => {
      const vs = p.variants.filter(v => v.available), v = vs.find(v => v.id === extraChoice[p.id]) || vs[0];
      return `<article class="supply-card ${extraQty[v.id] ? 'is-added' : ''}"><div class="supply-category">${esc(p.category)}</div><div class="kit-product-heading">${productThumb(p, v.id)}<h3><a href="${p.url}?variant=${v.id}" target="_blank" rel="noopener noreferrer" aria-label="Product details: ${esc(p.title)}">${esc(shortSupply(p.title))}</a></h3></div>${benefitLine(p, 110)}<div class="supply-options">${vs.length > 1 ? `<label class="field">Package<select data-extra-option="${p.id}" aria-label="Package for ${esc(p.title)}">${vs.map(x => `<option value="${x.id}" ${x.id === v.id ? 'selected' : ''}>${esc(x.title)}</option>`).join('')}</select></label>` : `<span class="pack-label">${v.title === 'Default Title' ? 'Sold as listed' : esc(v.title)}</span>`}</div>${stockMarkup(p, v.id)}<div class="supply-buy"><strong>${money(v.price)}</strong><button type="button" data-extra-add="${v.id}" class="${extraQty[v.id] ? 'added' : ''}">${extraQty[v.id] ? '+ Add another' : '+ Add'}</button></div></article>`;
    }).join('') || '<p class="empty-supplies">No available supplies match this search.</p>';
    el('aco-extra-more').innerHTML = found.length > extraLimit ? '<button type="button" data-extra-more>Show more supplies</button>' : '';
    const added = extras.filter(p => extraQty[p.id]);
    el('aco-extra-selected').innerHTML = added.length
      ? `<div class="added-supplies"><div class="added-heading"><h3>Added to your kit</h3><span class="pill">${added.reduce((n, p) => n + extraQty[p.id], 0)} items</span></div>${added.map(p => `<div class="added-item"><div><div class="kit-product-heading">${productThumb(p, p.id)}<strong>${esc(shortSupply(p.name))}</strong></div>${p.title === 'Default Title' ? '' : `<div class="note">${esc(p.title)}</div>`}${stockMarkup(p, p.id)}<button type="button" class="remove" data-extra-remove="${p.id}">Remove</button></div><input class="qty" type="number" aria-label="Quantity for ${esc(p.name + ' ' + p.title)}" min="1" max="99" data-extra-qty="${p.id}" value="${extraQty[p.id]}"><span class="added-price">${money(p.price * extraQty[p.id])}</span></div>`).join('')}</div>`
      : '<div class="empty-supplies"><span>No extra supplies added</span>Your selected supplies will appear here.</div>';
    total();
  }

  /* ---------- live product data ---------- */
  const productUrl = h => shopRoot + 'products/' + encodeURIComponent(h) + '?view=aco-stock';
  const normalVariants = p => p.variants.map(v => ({ ...v, id: String(v.id), price: v.price / 100 }));

  function repaint() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      drawPaint(); coatingSets.forEach(drawCoating); drawExtras();
      if (paint.source === 'custom' && el('aco-paint-search').value.trim() === customLoadedQuery) renderCustomResults();
    }, 60);
  }

  function adoptLive(p) {
    if (/\(POS\)/i.test(p.title)) { p.available = false; p.variants.forEach(v => v.available = false); }
    const pid = /^\d+$/.test(numericId(p.id)) ? numericId(p.id) : null;
    // Drop any stale entry for this product that was stored under an old handle.
    const previous = pid && liveById.get(pid)?.product;
    if (previous && previous.handle !== p.handle) liveProducts.delete(previous.handle);
    const entry = { product: p, at: Date.now() };
    liveProducts.set(p.handle, entry); if (pid) liveById.set(pid, entry);
    for (const v of p.variants) variantIndex.set(String(v.id), { product: p, variant: v });
    const vs = normalVariants(p);
    // Match seeds by product id first (handles in the catalog can go stale), then by handle.
    for (const seed of allSeed.filter(s => (pid && s.id && numericId(s.id) === pid) || s.handle === p.handle)) {
      seed.handle = p.handle;
      if (p.url) seed.url = location.origin + p.url;
      if (p.benefits) seed.benefits = p.benefits;
      seed.title = p.title; seed.description = p.description; seed.tags = p.tags || seed.tags; seed.live = true;
      if (seed.code) {
        seed.variants.forEach(v => { const actual = vs.find(a => a.id === numericId(v.id)); v.price = actual?.available ? actual.price : NaN; v.available = !!actual?.available; });
      } else seed.variants = vs;
      if (seed.kind === 'primer' || seed.kind === 'clear') seed.types = (seed.kind === 'primer' ? data.primerTypes : data.clearTypes).filter(t => (p.tags || []).some(tag => norm(tag.replace(/^Body Work Material_/i, '')) === norm(t)));
    }
    for (const x of extras.filter(s => (pid && numericId(s.productId) === pid) || s.handle === p.handle)) { const v = vs.find(v => v.id === x.id); x.price = v?.available ? v.price : NaN; x.available = !!v?.available; x.handle = p.handle; x.url = p.url ? location.origin + p.url : x.url; if (p.benefits) x.benefits = p.benefits; }
    const cp = custom.find(s => (pid && s.id === pid) || s.handle === p.handle);
    if (cp) { const parsed = customProduct(p); if (parsed) Object.assign(cp, parsed); else cp.variants.forEach(v => { v[4] = 0; v[3] = NaN; }); }
    if (p.handle === kitHandle) data.paintKit.variants.nodes = p.variants.map(v => ({ ...v, id: String(v.id), price: v.price / 100 }));
    return p;
  }

  // Loads every kit product in one request. Returns the set of handles it covered.
  async function applyFeed(feed) {
    const covered = new Set();
    if (!feed?.products) return covered;
    for (const p of feed.products) {
      const product = {
        id: p.id, handle: p.handle, title: p.title, available: p.available, tags: p.tags, url: p.url, benefits: p.benefits,
        featured_image: p.featured_image,
        variants: p.variants.map(v => ({ id: v.id, title: v.title, price: v.price, available: v.available, featured_image: v.featured_image })),
        aco_stock: p.variants.filter(v => v.message).map(v => ({ id: v.id, title: v.title, message: v.message }))
      };
      adoptLive(product);
      covered.add(p.handle);
    }
    return covered;
  }

  async function getProduct(handle, force = false) {
    const cached = liveProducts.get(handle);
    if (!force && cached && Date.now() - cached.at < 60000) return cached.product;
    if (pendingProducts.has(handle)) return pendingProducts.get(handle);
    const request = (async () => {
      try {
        const response = await fetch(productUrl(handle), { cache: 'no-store' });
        if (!response.ok) throw Error('Could not load ' + handle);
        const payload = await response.json();
        if (!payload.product?.variants || !Array.isArray(payload.stock)) throw Error('Stock view unavailable');
        payload.product.aco_stock = payload.stock;
        return adoptLive(payload.product);
      } catch (stockError) {
        const fallback = await fetch(shopRoot + 'products/' + encodeURIComponent(handle) + '.js', { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (!fallback.ok) throw stockError;
        return adoptLive(await fallback.json());
      }
    })();
    pendingProducts.set(handle, request);
    try { return await request; } finally { pendingProducts.delete(handle); }
  }
  function queueProduct(p) {
    if (!p?.handle || liveProducts.has(p.handle) || queued.has(p.handle)) return;
    queued.add(p.handle); queue.push(p.handle); drainQueue();
  }
  function drainQueue() {
    while (activeLoads < 4 && queue.length) {
      const h = queue.shift(); activeLoads++;
      getProduct(h).catch(() => {}).finally(() => { activeLoads--; repaint(); drainQueue(); });
    }
  }

  function productThumb(product, variantId) {
    const live = product?.handle ? liveProducts.get(product.handle)?.product : null;
    const variant = variantId && live?.variants.find(v => String(v.id) === numericId(variantId));
    const raw = variant?.featured_image?.src || live?.featured_image?.src || live?.featured_image || product?.featured_image?.src || product?.featured_image;
    let source = '';
    if (typeof raw === 'string' && raw) {
      try { const url = new URL(raw, location.origin); if (['https:', 'http:'].includes(url.protocol)) { url.searchParams.set('width', '112'); source = url.toString(); } } catch (e) {}
    }
    return `<span class="kit-thumb" aria-hidden="true"><svg viewBox="0 0 32 32" fill="none"><rect x="7" y="8" width="18" height="20" rx="3"/><path d="M10 8V5h12v3M7 14h18M12 21h8"/></svg>${source ? `<img data-kit-thumb src="${esc(source)}" alt="" width="56" height="56" loading="lazy" decoding="async">` : ''}</span>`;
  }
  root.addEventListener('error', e => { if (e.target.matches?.('img[data-kit-thumb]')) e.target.hidden = true; }, true);

  function stockMarkup(product, variantId) {
    const live = liveProducts.get(product?.handle)?.product;
    const item = live?.aco_stock?.find(x => String(x.id) === numericId(variantId));
    return item?.message ? `<p class="aco-low-stock" role="status">${esc(item.message)}</p>` : '';
  }
  function variantLine(id, qty = 1) {
    const hit = variantIndex.get(numericId(id));
    if (!hit) return null;
    const { product: p, variant: v } = hit;
    return { id: String(v.id), handle: p.handle, name: p.title, variant: v.title, price: v.price / 100, available: v.available, quantity: qty, benefits: p.benefits };
  }

  /* ---------- product benefits ---------- */
  function benefitsOf(product) {
    const live = product?.handle ? liveProducts.get(product.handle)?.product : null;
    const list = live?.benefits || product?.benefits || [];
    return Array.isArray(list) ? list.filter(Boolean) : [];
  }
  // One short line for tiles / cards.
  function benefitLine(product, max = 90) {
    const first = benefitsOf(product)[0] || '';
    return first ? `<span class="kit-blurb">${esc(first.length > max ? first.slice(0, max - 1).replace(/\s+\S*$/, '') + '…' : first)}</span>` : '';
  }
  // Custom-mixed OMNI paints share one description pattern (vehicle list), so their benefits are fixed per system.
  const customPaintBenefits = base => base
    ? ['Custom-mixed to your factory paint code for an accurate color match', 'OMNI MBC acrylic basecoat — fast-drying, easy to spray and blend', 'Mix 1:1 with OMNI MR186 reducer', 'Top-coat with a clearcoat for gloss and long-term protection']
    : ['Custom-mixed to your factory paint code for an accurate color match', 'OMNI MTK single-stage urethane — color and gloss in one step, no separate clear', 'Mix 4:1:1 with MH167 hardener and MR186 reducer', 'Durable finish for overall resprays and panel repairs'];
  // Bulleted list for the selected-product panel.
  function benefitList(product, heading = 'Why this product') {
    const items = benefitsOf(product).slice(0, 4);
    return items.length ? `<div class="kit-benefits"><strong>${heading}</strong><ul>${items.map(b => `<li>${esc(b)}</li>`).join('')}</ul></div>` : '';
  }

  /* ---------- companion (reducer / hardener) logic ---------- */
  function paintRecommendation() {
    let id, name, label, note;
    if (paint.source === 'factory') {
      id = paint.size === 4 ? '41092019388570' : '41092011983002'; name = 'High Teck 7720 Reducer'; label = paint.size === 4 ? 'Gallon' : 'Quart';
      note = 'One matching-size reducer for each paint container.';
    } else if (paint.kind === 'base') {
      id = paint.size === 4 ? '41268171374746' : '40155506081946'; name = 'OMNI MR186 Reducer'; label = paint.size === 4 ? 'MR186 GL' : 'MR186 QT';
      note = paint.size === .5 ? 'Quart is the smallest listed MR186 size; some reducer will remain.' : 'MR186 reducer matches the amount of MBC paint.';
    } else {
      id = paint.size === 4 ? '46517890252954' : '46517890351258'; name = 'OMNI MTK Kit'; label = paint.size === 4 ? 'MR186 (QT) & MH167 (QT)' : 'MR186 (QT) & MH167 (1/2 PT)';
      note = 'Reducer and hardener are included in this package. Extra supplies may remain.';
    }
    const line = variantLine(id);
    return { name, variant: label, price: line?.available ? line.price : NaN, note, id, handle: line?.handle };
  }

  function volume(s) {
    s = String(s).toLowerCase();
    if (/half\s*pint|1\/2\s*(?:pt|pint)/.test(s)) return .25;
    if (/(?:2\s*qt|2\s*quart)/.test(s)) return 2;
    if (/gallon|\bgal\b|1\s*gl\b|\(gl\)|-g01|\b1gl\b/.test(s)) return 4;
    if (/quart|\bqt\b|1\s*qt/.test(s)) return 1;
    if (/pint|\bpt\b|1\s*pt/.test(s)) return .5;
    if (/2\.5\s*l\b/.test(s)) return 2.64172;
    if (/0\.5\s*l\b/.test(s)) return .528344;
    if (/5\s*l\b/.test(s)) return 5.28344;
    if (/1\s*l\b/.test(s)) return 1.05669;
    return null;
  }

  function coatingRecipe(c) {
    const { p, v, s } = selected(c), t = p.title, vt = v.title;
    const plain = /^(?:Default Title|Quart|Gallon|Pint|None|No Hardener)$/i.test(vt);
    const included = !/^(none|no hardener)$/i.test(vt) && (!plain || /\bkit\b|w\s*\/|&|with.*(?:hardener|catalyst|activator)/i.test(t));
    const quantity = volume(vt) || volume(t), req = [];
    let missing = '';
    if (/MP282/.test(t)) { if (!included) req.push({ code: 'MH283', ratio: 4 }); }
    else if (/MP23[157X]/.test(t)) { if (!included) req.push({ code: 'MH283', ratio: 4 }, { code: 'MR186', ratio: 4 }); }
    else if (included || ['none', 'internal'].includes(p.mix.companion) || /100712/.test(t)) { /* nothing extra required */ }
    else if (/MP3-HS/.test(t)) req.push({ code: 'MA3-HS', ratio: 4 });
    else if (/MP17[012]/.test(t)) req.push({ code: 'MP175', ratio: 2 });
    else if (/7755[13]/.test(t)) req.push({ code: '77557', ratio: 4 });
    else if (/7740[0134]/.test(t)) req.push({ code: '7720', ratio: 1 });
    else if (/MP213/.test(t)) req.push({ code: 'MR186', ratio: 4 });
    else if (/MC161|MC260/.test(t)) req.push({ code: 'MH167', ratio: 2 });
    else if (/10227[34]/.test(t)) missing = 'Evercoat #2296 reducer';
    else if (/77165/.test(t)) missing = 'High Teck 7102 activator';
    else if (/CH205[135]|CH3050/.test(t)) missing = 'Challenger CH0060 hardener';
    else if (/MP-2K/.test(t)) missing = 'MATRIX Quick Sand matching activator';
    else missing = p.mix.companion && p.mix.companion !== 'hardener' ? p.mix.companion : 'the matching activator shown in the product instructions';

    const lines = [];
    for (const r of req) {
      const handles = data.companions.filter(p => norm(p.title + p.handle).includes(norm(r.code))).map(p => p.handle);
      const choices = [];
      for (const h of handles) {
        const live = liveProducts.get(h)?.product;
        if (!live) continue;
        for (const vv of live.variants.filter(v => v.available)) {
          const size = volume(vv.title) || volume(live.title) || volume(live.handle.replace(/-/g, ' '));
          if (size) choices.push({ ...variantLine(vv.id), size });
        }
      }
      const need = quantity && quantity * s.qty / r.ratio;
      if (!need || !choices.length) { missing = r.code; continue; }
      // Prefer the package combination with the least waste, then the fewest containers.
      choices.sort((a, b) => { const aw = Math.ceil(need / a.size) * a.size, bw = Math.ceil(need / b.size) * b.size; return aw - bw || Math.ceil(need / a.size) - Math.ceil(need / b.size); });
      const key = c.id + ':' + r.code, choice = choices.find(v => v.id === companionChoices[key]) || choices[0];
      lines.push({ ...choice, quantity: Math.ceil(need / choice.size), choices, key, code: r.code });
    }
    let alternative = null;
    if (missing) {
      const family = /77165/.test(t) ? '77165' : /MP-2K/.test(t) ? 'MATRIX Quick Sand' : null;
      if (family) alternative = c.products.find(x => x.id !== p.id && x.title.includes(family) && /kit|w\//i.test(x.title) && (!quantity || volume(x.title) === quantity));
    }
    return { lines, missing, alternative, required: !!(req.length || missing) };
  }
  function coatingSuppliesIncluded(c) { return companionIncluded[c.id] && coatingRecipe(c).lines.every(l => !removedKitItems.has(String(l.id))); }
  function setCoatingSuppliesIncluded(c, include) { companionIncluded[c.id] = include; if (include) coatingRecipe(c).lines.forEach(l => removedKitItems.delete(String(l.id))); }
  function companionMarkup(c) {
    const r = coatingRecipe(c);
    if (!r.required) return '';
    return `<label class="include-choice"><input type="checkbox" data-coat-companion="${c.id}" ${coatingSuppliesIncluded(c) ? 'checked' : ''}>Add matching supplies</label>`
      + r.lines.map(l => `<div class="kit-product-heading kit-companion-photo">${productThumb(l, l.id)}<p>${l.quantity} × ${esc(l.name)}${l.variant === 'Default Title' ? '' : ' · ' + esc(l.variant)} — ${money(l.price * l.quantity)}</p></div>${stockMarkup(l, l.id)}${l.choices.length > 1 ? `<label>Substitute package<select data-companion-option="${esc(l.key)}">${l.choices.map(v => `<option value="${v.id}" ${v.id === l.id ? 'selected' : ''}>${esc(v.name + ' · ' + v.variant)} — ${money(v.price)}</option>`).join('')}</select></label>` : ''}`).join('')
      + (r.missing ? `<p class="error">${esc(r.missing)} is required but an available matching package could not be confirmed. ${r.alternative ? 'Choose the complete kit below.' : 'Choose another product, or uncheck matching supplies if you already have it.'}</p>${r.alternative ? `<button type="button" data-coating-pick="${c.id}" data-id="${r.alternative.id}">Choose complete kit</button>` : ''}` : '')
      + (!companionIncluded[c.id] ? '<p>Product only. You will need the matching supplies to mix it.</p>' : '');
  }
  function coatingCost(c) {
    const { s, v } = selected(c), r = coatingRecipe(c);
    return v.price * s.qty + (companionIncluded[c.id] ? (r.missing ? NaN : r.lines.filter(l => !removedKitItems.has(String(l.id))).reduce((n, l) => n + l.price * l.quantity, 0)) : 0);
  }

  /* ---------- kit lines ---------- */
  // Returns null while required live data is missing (prices still loading / product unavailable).
  function selectedLines(includeRemoved = false) {
    const lines = [];
    if (paint.on && paintProduct()) {
      const id = paintVariantId();
      if (!id) return null;
      const line = variantLine(id, paint.qty);
      if (!line) return null;
      lines.push(line);
      if (paint.include) { const r = variantLine(paintRecommendation().id, paint.qty); if (!r) return null; lines.push(r); }
    }
    for (const c of coatingSets) {
      const { s, v } = selected(c);
      if (!s.on) continue;
      const line = variantLine(v.id, s.qty);
      if (!line) return null;
      lines.push(line);
      if (companionIncluded[c.id]) { const r = coatingRecipe(c); if (r.missing) return null; lines.push(...r.lines); }
    }
    for (const x of extras.filter(x => extraQty[x.id])) {
      const line = variantLine(x.id, extraQty[x.id]);
      if (!line) return null;
      lines.push(line);
    }
    return includeRemoved ? lines : lines.filter(l => !removedKitItems.has(String(l.id)));
  }
  function selectionReady() { const lines = selectedLines(); return !!lines?.length && lines.every(l => l.available && Number.isFinite(l.price)); }

  async function addKitToCart() {
    if (busy) return;
    busy = true; total();
    el('aco-status').textContent = 'Checking current prices and availability…';
    try {
      let lines = selectedLines();
      if (!lines?.length) throw Error('Choose available products and matching supplies first.');
      const oldPrice = lines.reduce((n, l) => n + l.quantity * l.price, 0);
      // Re-check prices and stock: one feed request, then per-product only for anything the feed did not cover.
      const covered = await applyFeed(await fetchFeed(true));
      await Promise.all([...new Set(lines.map(l => l.handle))].filter(h => !covered.has(h)).map(h => getProduct(h, true)));
      lines = selectedLines();
      if (!lines?.length || lines.some(l => !l.available)) throw Error('A selected product is no longer available. Please review your kit.');
      const price = lines.reduce((n, l) => n + l.quantity * l.price, 0);
      if (Math.abs(oldPrice - price) > .005) throw Error('A product price changed. Your subtotal is updated; review it and add your kit again.');
      const combined = new Map();
      for (const l of lines) combined.set(l.id, (combined.get(l.id) || 0) + l.quantity);
      const items = [...combined].map(([id, quantity]) => ({ id: Number(id), quantity, properties: { _aco_kit_builder: '2026' } }));
      const response = await fetch(shopRoot + 'cart/add.js?revy-request=aco-kit-builder', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
      const result = await response.json();
      if (!response.ok) { el('aco-status').innerHTML = esc(result.description || 'Some quantities could not be added.') + ' <a href="' + shopRoot + 'cart">Review cart</a>'; return; }
      el('aco-status').innerHTML = 'Your kit has been added. <a href="' + shopRoot + 'cart">View cart and checkout</a>';
      root.dispatchEvent(new CustomEvent('aco:kit-added', { bubbles: true, detail: { items: result.items } }));
      const cart = await fetch(shopRoot + 'cart.js', { cache: 'no-store' }).then(r => r.json());
      document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(n => n.textContent = cart.item_count);
      document.querySelectorAll('.cart-count-bubble').forEach(n => n.hidden = false);
    } catch (e) {
      el('aco-status').textContent = e.message || 'The cart could not be reached. Check your cart before trying again.';
      repaint();
    } finally { busy = false; total(); }
  }

  /* ---------- custom paint search ---------- */
  function customProduct(p) {
    if (/\(POS\)/i.test(p.title) || !p.available || !/MBC.*MTK|MTK.*MBC/i.test(p.title)) return null;
    const heading = p.title.split(/\s[–—]\s|\s-\s(?=MBC|MTK)/)[0].trim();
    const known = custom.find(x => x.handle === p.handle);
    const make = known?.make || [...new Set(custom.map(x => x.make))].sort((a, b) => b.length - a.length).find(m => heading.toLowerCase().startsWith(m.toLowerCase() + ' ')) || heading.split(/\s+/)[0];
    const rest = heading.slice(make.length).trim(), split = rest.indexOf(' ');
    if (split <= 0) return null;
    const variants = p.variants.map(v => {
      const kind = /MBC|basecoat/i.test(v.title) ? 0 : /MTK|single.stage/i.test(v.title) ? 1 : 2;
      return [String(v.id), kind, volume(v.title) || 0, v.price / 100, v.available ? 1 : 0];
    });
    return { id: String(p.id), make, code: rest.slice(0, split).replace(/,$/, ''), color: rest.slice(split + 1), handle: p.handle, variants, key: norm(heading), live: true, benefits: p.benefits };
  }
  function mergeCustom(cp) {
    const old = custom.find(x => x.id === cp.id);
    if (old) { Object.assign(old, cp); return old; }
    custom.push(cp); return cp;
  }
  async function searchLive(q) {
    const url = new URL(shopRoot + 'search', location.origin);
    url.search = new URLSearchParams({ view: 'aco-kit', type: 'product', q, 'options[unavailable_products]': 'hide', 'options[prefix]': 'last' });
    const response = await fetch(url.toString(), { cache: 'no-store' });
    if (!response.ok) throw Error('Search unavailable');
    const first = await response.json();
    let products = first.products;
    for (let page = 2; page <= Math.min(first.pages, 25); page++) {
      url.searchParams.set('page', page);
      const r = await fetch(url.toString(), { cache: 'no-store' });
      if (!r.ok) throw Error('Search unavailable');
      products = products.concat((await r.json()).products);
    }
    return products;
  }
  function customResults() {
    clearTimeout(customTimer);
    const q = el('aco-paint-search').value.trim(), request = ++customRequest;
    if (!q) { customLoadedQuery = ''; renderCustomResults(); return; }
    if (q === customLoadedQuery) { renderCustomResults(); return; }
    el('aco-custom-results').textContent = 'Searching available paint colors…';
    customTimer = setTimeout(async () => {
      try {
        const found = await searchLive(q);
        if (request !== customRequest) return;
        for (const p of found) { adoptLive(p); const cp = customProduct(p); if (cp) mergeCustom(cp); }
        const ids = new Set(found.map(p => String(p.id)));
        for (const p of custom) p.searchMatch = ids.has(p.id);
        customLoadedQuery = q;
        renderCustomResults();
      } catch (e) {
        if (request === customRequest) el('aco-custom-results').innerHTML = 'Search could not be loaded. Try again or <a href="' + data.finder + '">open OEM Vehicle Paint Finder</a>.';
      }
    }, 300);
  }

  /* ---------- steps ---------- */
  const steps = ['primer', 'paint', 'clear', 'extras'];
  const stepLabels = { primer: 'Primer', paint: 'Paint', clear: 'Clear', extras: 'Anything else' };
  let activeStep = 'primer';
  function showStep(id) {
    activeStep = id;
    for (const step of steps) { el('aco-pane-' + step).hidden = step !== id; el('aco-tab-' + step).setAttribute('aria-selected', String(step === id)); }
    const index = steps.indexOf(id);
    el('aco-back').hidden = index === 0;
    el('aco-next').hidden = index === steps.length - 1;
    el('aco-next').textContent = 'Next: ' + (stepLabels[steps[index + 1]] || '');
  }
  function navigateStep(id) {
    showStep(id);
    requestAnimationFrame(() => {
      if (activeStep !== id) return;
      const tab = el('aco-tab-' + id), navigation = tab.closest('[role="tablist"]');
      const header = document.querySelector('sticky-header')?.closest('.shopify-section');
      navigation.style.scrollMarginTop = ((header?.offsetHeight || 0) + 16) + 'px';
      tab.focus({ preventScroll: true });
      navigation.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start', inline: 'nearest' });
    });
  }

  function kitSummary() {
    const all = selectedLines(true);
    if (!all) { el('aco-summary-lines').innerHTML = '<div class="summary-row"><p>Finish your product selections to review the kit.</p></div>'; return; }
    const merged = new Map();
    for (const line of all) { const id = String(line.id); if (merged.has(id)) merged.get(id).quantity += line.quantity; else merged.set(id, { ...line }); }
    const rows = [...merged.values()].filter(line => !removedKitItems.has(String(line.id))).map(line => `<div class="summary-row kit-summary-item"><div class="kit-summary-item__body">${productThumb(line, line.id)}<div><p>${esc(line.name)}</p><span class="note">${line.quantity} × ${esc(line.variant === 'Default Title' ? 'item' : line.variant)}</span>${stockMarkup(line, line.id)}</div></div><div class="kit-summary-item__actions"><strong>${money(line.price * line.quantity)}</strong><button type="button" data-kit-remove="${esc(line.id)}" aria-label="Remove ${esc(line.name)} from kit" ${busy ? 'disabled' : ''}>Remove</button></div></div>`);
    const removed = [...merged.values()].filter(line => removedKitItems.has(String(line.id)));
    if (removed.length) rows.push(`<details class="kit-summary-removed"><summary>Removed items (${removed.length})</summary>${removed.map(line => `<div><span>${esc(line.name)}</span><button type="button" data-kit-restore="${esc(line.id)}" aria-label="Restore ${esc(line.name)} to kit" ${busy ? 'disabled' : ''}>Restore</button></div>`).join('')}<p class="note">Removed products are not included. You’ll still need any required reducer or hardener to mix your coatings.</p></details>`);
    el('aco-summary-lines').innerHTML = rows.join('') || '<div class="summary-row"><p>Choose products to start your kit.</p></div>';
  }

  /* ---------- entry from a product page (?product=handle&variant=id) ---------- */
  async function startLive() {
    coatingSets.flatMap(c => c.products).filter(p => !p.variants.some(v => v.available)).forEach(queueProduct);
    const required = [...coatingSets.filter(c => states[c.id].on).map(c => selected(c).p.handle), ...data.companions.map(p => p.handle), kitHandle];
    await Promise.allSettled(required.map(h => getProduct(h)));
    repaint();
    el('aco-status').textContent = 'Review your selections, then add the kit to your cart.';

    const params = new URLSearchParams(location.search), handle = params.get('product'), variant = params.get('variant');
    if (!handle) return;
    try {
      const p = await getProduct(handle);
      if (!p.available || /\(POS\)/i.test(p.title)) throw Error('This product is not available for the kit builder.');
      const f = data.factory.find(x => x.handle === handle), co = data.coatings.find(x => x.handle === handle), cp = customProduct(p), extra = data.accessories.find(x => x.handle === handle);
      if (f) {
        Object.assign(paint, { on: true, source: 'factory', factory: f.code, size: f.variants.find(v => numericId(v.id) === variant)?.size || f.variants[0].size });
        root.querySelector('[name="aco-source"][value="factory"]').checked = true;
        showStep('paint');
      } else if (co) {
        const s = states[co.kind];
        s.on = true; s.id = co.id; s.variant = Math.max(0, co.variants.filter(v => v.available).findIndex(v => v.id === variant));
        showStep(co.kind);
      } else if (cp) {
        const entry = mergeCustom(cp);
        const v = cp.variants.find(v => v[0] === variant && v[1] < 2) || cp.variants.find(v => v[1] < 2 && v[4]);
        if (!v) throw Error('Choose a basecoat or single-stage paint size.');
        Object.assign(paint, { on: true, source: 'custom', custom: entry, kind: v[1] === 1 ? 'single' : 'base', size: v[2] });
        root.querySelector('[name="aco-source"][value="custom"]').checked = true;
        root.querySelector('[name="aco-kind"][value="' + paint.kind + '"]').checked = true;
        showStep('paint');
      } else if (extra) {
        const v = extra.variants.find(v => v.id === variant && v.available) || extra.variants.find(v => v.available);
        if (v) { extraQty[v.id] = 1; extraChoice[extra.id] = v.id; }
        showStep('extras');
      } else {
        const v = p.variants.find(v => String(v.id) === variant && v.available) || p.variants.find(v => v.available);
        if (v) {
          const entry = { id: String(p.id), handle: p.handle, title: p.title, url: shopRoot + 'products/' + p.handle, category: 'Additional products', variants: normalVariants(p) };
          extraProducts.push(entry); allSeed.push(entry);
          extras.push(...entry.variants.map(v => ({ ...v, productId: entry.id, name: entry.title, category: entry.category, url: entry.url, handle: entry.handle })));
          extraQty[String(v.id)] = 1; extraChoice[entry.id] = String(v.id);
          showStep('extras');
        }
      }
      repaint();
    } catch (e) { el('aco-status').textContent = e.message; }
  }

  /* ---------- events ---------- */
  root.addEventListener('input', e => {
    const t = e.target, k = t.dataset;
    if (t.id === 'aco-paint-search') { resultLimit = 8; if (paint.source === 'factory') factoryTiles(); else customResults(); }
    if (k.coatingSearch) { states[k.coatingSearch].limit = 6; drawCoatingTiles(coatingSets.find(c => c.id === k.coatingSearch)); }
    if (t.id === 'aco-extra-search') { extraLimit = 6; drawExtras(); }
  });

  root.addEventListener('change', e => {
    const t = e.target, k = t.dataset;
    // extras
    if (t.id === 'aco-extra-type') { extraLimit = 6; drawExtras(); }
    if (k.extraOption) { extraChoice[k.extraOption] = t.value; drawExtras(); }
    if (k.extraQty) { extraQty[k.extraQty] = clamp(t.value); drawExtras(); }
    // paint
    if (t.id === 'aco-paint-on') {
      if (t.checked && !paintProduct()) { t.checked = false; el('aco-status').textContent = 'Choose a color below to include paint in your kit.'; return; }
      paint.on = t.checked; drawPaint();
    }
    if (t.name === 'aco-source') {
      paint.source = t.value; el('aco-paint-search').value = '';
      paint.on = !!paintProduct();
      drawPaint();
      if (paint.source === 'custom') customResults();
    }
    if (t.name === 'aco-kind') { paint.kind = t.value; drawPaint(); if (paint.source === 'custom') customResults(); }
    if (t.hasAttribute('data-paint-size')) { paint.size = Number(t.value); drawPaint(); }
    if (t.hasAttribute('data-paint-qty')) { paint.qty = clamp(t.value); drawPaint(); }
    if (t.hasAttribute('data-paint-include')) { setPaintSuppliesIncluded(t.checked); drawPaint(); }
    // coatings
    const id = k.coatingOn || k.coatingType || k.coatingVariant || k.coatingQty;
    if (id) {
      const c = coatingSets.find(c => c.id === id), s = states[id];
      if (k.coatingOn) s.on = t.checked;
      if (k.coatingType) { s.type = t.value; s.limit = 6; }
      if (k.coatingVariant) s.variant = Number(t.value);
      if (k.coatingQty) s.qty = clamp(t.value);
      drawCoating(c);
    }
    if (k.coatCompanion) { const c = coatingSets.find(c => c.id === k.coatCompanion); setCoatingSuppliesIncluded(c, t.checked); drawCoating(c); }
    if (k.companionOption) { companionChoices[k.companionOption] = t.value; coatingSets.forEach(drawCoating); }
  });

  root.addEventListener('click', e => {
    const b = e.target.closest('button');
    if (!b) return;
    const k = b.dataset;
    // steps
    if (k.step) showStep(k.step);
    if (b.id === 'aco-back') navigateStep(steps[Math.max(0, steps.indexOf(activeStep) - 1)]);
    if (b.id === 'aco-next') navigateStep(steps[Math.min(steps.length - 1, steps.indexOf(activeStep) + 1)]);
    // paint
    if (k.factory) choosePaint({ source: 'factory', factory: k.factory });
    if (k.custom) choosePaint({ source: 'custom', custom: custom.find(p => p.id === k.custom) });
    if (b.hasAttribute('data-more-paint')) { resultLimit += 8; customResults(); }
    // coatings
    if (k.coatingPick) { const c = coatingSets.find(c => c.id === k.coatingPick), s = states[c.id]; s.id = k.id; s.variant = defaultVariant(selected(c).p); s.on = true; drawCoating(c); }
    if (k.moreCoating) { const c = coatingSets.find(c => c.id === k.moreCoating); states[c.id].limit += 6; drawCoatingTiles(c); }
    // extras
    if (b.hasAttribute('data-extra-more')) { extraLimit += 6; drawExtras(); }
    if (k.extraAdd) { extraQty[k.extraAdd] = Math.min(99, (extraQty[k.extraAdd] || 0) + 1); drawExtras(); }
    if (k.extraRemove) { delete extraQty[k.extraRemove]; drawExtras(); }
    // summary remove / restore
    if ((k.kitRemove || k.kitRestore) && !busy) {
      const id = k.kitRemove || k.kitRestore;
      if (k.kitRemove) { const line = selectedLines(true)?.find(line => String(line.id) === id); if (line) removedKitItems.set(id, line.name); }
      else removedKitItems.delete(id);
      drawPaint(); coatingSets.forEach(drawCoating); total();
      el('aco-status').textContent = k.kitRemove ? 'Product removed from your kit.' : 'Product restored to your kit.';
    }
    if (b.id === 'aco-cart') addKitToCart();
  });

  root.addEventListener('keydown', e => {
    const b = e.target.closest('[data-step]');
    if (!b || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const n = steps.length, current = steps.indexOf(b.dataset.step);
    const index = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (current + (e.key === 'ArrowRight' ? 1 : n - 1)) % n;
    showStep(steps[index]);
    el('aco-tab-' + steps[index]).focus();
  });

  /* ---------- boot ---------- */
  // Wait for the single feed request so the first paint already has images, prices and stock.
  await applyFeed(await feedRequest);
  drawPaint(); coatingSets.forEach(drawCoating); drawExtras(); showStep('primer'); startLive();
})();
